/**
 * PromptClassificationService - Core orchestration service for prompt categorization
 * 
 * This service provides:
 * - Synchronous classification with timeout monitoring (1000ms threshold)
 * - Asynchronous processing fallback for long-running operations
 * - Integration with CategoryLoader, MistralApiService, and PromptEngineeringService
 * - Database operations for storing classification results
 * - Performance monitoring and health checks
 * - Comprehensive error handling and recovery strategies
 * - Configurable timeout and processing settings
 * 
 * Architecture:
 * - Dependency injection for testability and modularity
 * - Observer pattern for monitoring hooks
 * - State machine for processing mode management
 * - Queue-based background processing for async operations
 * 
 * Usage:
 * const service = new PromptClassificationService(config, dependencies);
 * await service.classifyPrompt(promptId, promptText);
 */

import { supabase } from '@/integrations/supabase/client';
import { CategoryLoader, CategoryLogger, LogLevel } from './category-loader';
import { MistralApiService } from './mistral-api-service';
import { PromptEngineeringService, ClassificationPromptRequest, ParsedClassificationResult, ClassificationPromptResponse } from './prompt-engineering';
import type { Category } from '@/types/categories';
import type { ClassificationRequest, ClassificationResponse } from '@/types/mistral';
import { 
  getClassificationLogger, 
  ClassificationContext as LoggingContext, 
  ClassificationEventType, 
  PerformanceMetrics as LoggingPerformanceMetrics,
  ClassificationLogLevel
} from './classification-logger';
import { performanceMonitor } from './performance-monitor';
import { 
  processingOptimizer, 
  ProcessingMode as OptimizerMode,
  RequestComplexityFactors,
  ProcessingRecommendation 
} from './processing-optimizer';
import { 
  performanceAlerting,
  AlertNotificationHandler,
  PerformanceAlert
} from './performance-alerting';
import { QuickLoggerFactory } from '@/lib/universal-logger';

/**
 * Processing modes for classification operations
 */
export enum ProcessingMode {
  SYNC = 'sync',
  ASYNC = 'async',
  FAILED = 'failed'
}

/**
 * Classification operation status
 */
export enum ClassificationStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  TIMEOUT = 'timeout'
}

/**
 * Configuration interface for the classification service
 */
export interface ClassificationServiceConfig {
  /** Timeout in milliseconds for synchronous processing */
  syncTimeoutMs: number;
  /** Maximum retries for failed classifications */
  maxRetries: number;
  /** Retry delay multiplier for exponential backoff */
  retryDelayMultiplier: number;
  /** Maximum delay between retries in milliseconds */
  maxRetryDelayMs: number;
  /** Whether to enable async fallback when sync times out */
  enableAsyncFallback: boolean;
  /** Maximum concurrent async operations */
  maxConcurrentAsync: number;
  /** Default category ID for failed classifications */
  defaultCategoryId: number;
  /** Whether to store classification metrics */
  enableMetrics: boolean;
  /** Log level for classification operations */
  logLevel: LogLevel;
}

/**
 * Dependencies interface for dependency injection
 */
export interface ClassificationServiceDependencies {
  /** Category loader instance */
  categoryLoader?: CategoryLoader;
  /** Mistral API service instance */
  mistralApiService?: MistralApiService;
  /** Prompt engineering service instance */
  promptEngineeringService?: PromptEngineeringService;
  /** Logger instance */
  logger?: CategoryLogger;
  /** Custom database client for testing */
  supabaseClient?: typeof supabase;
  /** Performance timing function */
  performanceNow?: () => number;
}

/**
 * Classification request context for tracking operations
 */
export interface ClassificationContext {
  /** Unique request ID for tracking */
  requestId: string;
  /** Prompt ID from database */
  promptId: string;
  /** Original prompt text */
  promptText: string;
  /** Processing mode used */
  processingMode: ProcessingMode;
  /** Operation start time */
  startTime: number;
  /** Number of retry attempts */
  attempts: number;
  /** Classification status */
  status: ClassificationStatus;
  /** Associated category ID if classified */
  categoryId?: number;
  /** Associated category name if classified */
  categoryName?: string;
  /** Any error that occurred */
  error?: Error;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

/**
 * Classification result interface
 */
export interface ClassificationResult {
  /** Request context */
  context: ClassificationContext;
  /** Whether classification was successful */
  success: boolean;
  /** Assigned category ID */
  categoryId: number;
  /** Assigned category name */
  categoryName: string;
  /** Processing time in milliseconds */
  processingTimeMs: number;
  /** Processing mode used */
  processingMode: ProcessingMode;
  /** Confidence score if available */
  confidence?: number;
  /** Any error that occurred */
  error?: Error;
}

/**
 * Service metrics interface with comprehensive performance monitoring
 */
export interface ClassificationServiceMetrics {
  /** Total classification requests */
  totalRequests: number;
  /** Successful classifications */
  successfulClassifications: number;
  /** Failed classifications */
  failedClassifications: number;
  /** Sync mode successes */
  syncModeSuccesses: number;
  /** Async mode successes */
  asyncModeSuccesses: number;
  /** Timeout occurrences */
  timeouts: number;
  /** Total retry attempts */
  totalRetries: number;
  /** Average processing time for sync operations */
  averageSyncTimeMs: number;
  /** Average processing time for async operations */
  averageAsyncTimeMs: number;
  /** Category distribution */
  categoryDistribution: Record<number, number>;
  /** Current async queue size */
  asyncQueueSize: number;
  /** Service health status */
  isHealthy: boolean;
  /** Performance percentiles for sync operations */
  syncLatencyPercentiles: LatencyPercentiles;
  /** Performance percentiles for async operations */
  asyncLatencyPercentiles: LatencyPercentiles;
  /** Processing mode distribution */
  processingModeDistribution: ProcessingModeStats;
  /** API usage statistics */
  apiUsageStats: ApiUsageStats;
  /** Error statistics by type */
  errorStats: ErrorStats;
  /** Health check history */
  healthCheckHistory: HealthCheckResult[];
  /** Uptime statistics */
  uptimeStats: UptimeStats;
}

/**
 * Latency percentiles for performance analysis
 */
export interface LatencyPercentiles {
  p50: number;
  p75: number;
  p90: number;
  p95: number;
  p99: number;
  min: number;
  max: number;
}

/**
 * Processing mode statistics
 */
export interface ProcessingModeStats {
  syncCount: number;
  asyncCount: number;
  failedCount: number;
  syncSuccessRate: number;
  asyncSuccessRate: number;
  avgSyncToAsyncFallbackTime: number;
}

/**
 * API usage and cost tracking
 */
export interface ApiUsageStats {
  totalApiCalls: number;
  successfulApiCalls: number;
  failedApiCalls: number;
  totalTokensUsed: number;
  estimatedCosts: number;
  averageTokensPerRequest: number;
  apiErrorDistribution: Record<string, number>;
  rateLimitHits: number;
  avgApiResponseTime: number;
}

/**
 * Error statistics by category
 */
export interface ErrorStats {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsByCategory: Record<string, number>;
  recentErrors: Array<{
    timestamp: number;
    type: string;
    message: string;
    context?: string;
  }>;
  errorRate: number;
  mtbf: number; // Mean Time Between Failures (minutes)
}

/**
 * Health check result structure
 */
export interface HealthCheckResult {
  timestamp: number;
  isHealthy: boolean;
  status: string;
  responseTimeMs: number;
  details: Record<string, any>;
}

/**
 * Uptime and availability statistics
 */
export interface UptimeStats {
  serviceStartTime: number;
  totalUptimeMs: number;
  totalDowntimeMs: number;
  uptimePercentage: number;
  lastHealthyTimestamp: number;
  lastUnhealthyTimestamp?: number;
  healthCheckCount: number;
  consecutiveHealthyChecks: number;
  consecutiveUnhealthyChecks: number;
}

/**
 * Monitoring hook interface for observing operations
 */
export interface ClassificationMonitoringHook {
  /** Called when classification starts */
  onClassificationStart?(context: ClassificationContext): void;
  /** Called when classification completes */
  onClassificationComplete?(result: ClassificationResult): void;
  /** Called when switching to async mode */
  onAsyncModeSwitch?(context: ClassificationContext): void;
  /** Called when retry occurs */
  onRetry?(context: ClassificationContext, attempt: number): void;
  /** Called when classification fails */
  onClassificationFailure?(context: ClassificationContext, error: Error): void;
}

/**
 * Background task for async processing
 */
interface AsyncClassificationTask {
  context: ClassificationContext;
  retryAttempt: number;
  scheduledTime: number;
  processingRecommendation?: ProcessingRecommendation;
}

/**
 * Custom error classes for classification service
 */
export class ClassificationServiceError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: string
  ) {
    super(message);
    this.name = 'ClassificationServiceError';
  }
}

export class ClassificationTimeoutError extends ClassificationServiceError {
  constructor(timeoutMs: number, context?: string) {
    super(`Classification timeout after ${timeoutMs}ms`, 'CLASSIFICATION_TIMEOUT', context);
    this.name = 'ClassificationTimeoutError';
  }
}

export class ClassificationDatabaseError extends ClassificationServiceError {
  constructor(message: string, context?: string) {
    super(message, 'DATABASE_ERROR', context);
    this.name = 'ClassificationDatabaseError';
  }
}

export class ClassificationValidationError extends ClassificationServiceError {
  constructor(message: string, context?: string) {
    super(message, 'VALIDATION_ERROR', context);
    this.name = 'ClassificationValidationError';
  }
}

export class ClassificationApiError extends ClassificationServiceError {
  constructor(message: string, public readonly statusCode?: number, context?: string) {
    super(message, 'API_ERROR', context);
    this.name = 'ClassificationApiError';
  }
}

export class ClassificationServiceUnavailableError extends ClassificationServiceError {
  constructor(serviceName: string, context?: string) {
    super(`Service unavailable: ${serviceName}`, 'SERVICE_UNAVAILABLE', context);
    this.name = 'ClassificationServiceUnavailableError';
  }
}

export class ClassificationRateLimitError extends ClassificationServiceError {
  constructor(retryAfterMs?: number, context?: string) {
    super(`Rate limit exceeded${retryAfterMs ? `, retry after ${retryAfterMs}ms` : ''}`, 'RATE_LIMIT', context);
    this.name = 'ClassificationRateLimitError';
  }
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: ClassificationServiceConfig = {
  syncTimeoutMs: 1000,
  maxRetries: 3,
  retryDelayMultiplier: 2,
  maxRetryDelayMs: 10000,
  enableAsyncFallback: true,
  maxConcurrentAsync: 5,
  defaultCategoryId: 15, // "Other" category
  enableMetrics: true,
  logLevel: LogLevel.INFO
};

/**
 * Core PromptClassificationService class
 */
export class PromptClassificationService {
  private readonly config: ClassificationServiceConfig;
  private readonly categoryLoader: CategoryLoader;
  private readonly mistralApiService: MistralApiService;
  private readonly promptEngineeringService: PromptEngineeringService;
  private readonly logger: CategoryLogger;
  private readonly classificationLogger = getClassificationLogger();
  private readonly databaseLogger = QuickLoggerFactory.database();
  private readonly supabaseClient: typeof supabase;
  private readonly performanceNow: () => number;
  
  // Service state
  private readonly metrics: ClassificationServiceMetrics;
  private readonly monitoringHooks: ClassificationMonitoringHook[] = [];
  private readonly asyncQueue: AsyncClassificationTask[] = [];
  private currentAsyncOperations: number = 0;
  private isShuttingDown: boolean = false;
  
  // Performance tracking
  private syncProcessingTimes: number[] = [];
  private asyncProcessingTimes: number[] = [];
  private readonly maxTimingSamples = 100;
  
  // Advanced metrics tracking
  private apiResponseTimes: number[] = [];
  private errorHistory: Array<{
    timestamp: number;
    type: string;
    message: string;
    context?: string;
  }> = [];
  private syncToAsyncFallbackTimes: number[] = [];
  private lastHealthCheck: HealthCheckResult | null = null;
  private readonly maxErrorHistory = 50;
  private readonly maxHealthCheckHistory = 100;

  /**
   * Constructor with dependency injection
   * @param config Service configuration
   * @param dependencies Service dependencies for testing/customization
   */
  constructor(
    config: Partial<ClassificationServiceConfig> = {},
    dependencies: ClassificationServiceDependencies = {}
  ) {
    // Merge configuration with defaults
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Initialize dependencies with fallbacks
    this.categoryLoader = dependencies.categoryLoader || CategoryLoader.getInstance();
    this.logger = dependencies.logger || CategoryLogger.getInstance();
    this.supabaseClient = dependencies.supabaseClient || supabase;
    this.performanceNow = dependencies.performanceNow || (() => performance.now());
    
    // Initialize service dependencies
    if (!dependencies.mistralApiService) {
      throw new ClassificationServiceError(
        'MistralApiService is required but not provided',
        'MISSING_DEPENDENCY',
        'Constructor'
      );
    }
    this.mistralApiService = dependencies.mistralApiService;
    
    this.promptEngineeringService = dependencies.promptEngineeringService || 
      new PromptEngineeringService(this.categoryLoader, this.logger);
    
    // Initialize metrics
    this.metrics = {
      totalRequests: 0,
      successfulClassifications: 0,
      failedClassifications: 0,
      syncModeSuccesses: 0,
      asyncModeSuccesses: 0,
      timeouts: 0,
      totalRetries: 0,
      averageSyncTimeMs: 0,
      averageAsyncTimeMs: 0,
      categoryDistribution: {},
      asyncQueueSize: 0,
      isHealthy: true,
      syncLatencyPercentiles: {
        p50: 0, p75: 0, p90: 0, p95: 0, p99: 0, min: 0, max: 0
      },
      asyncLatencyPercentiles: {
        p50: 0, p75: 0, p90: 0, p95: 0, p99: 0, min: 0, max: 0
      },
      processingModeDistribution: {
        syncCount: 0,
        asyncCount: 0,
        failedCount: 0,
        syncSuccessRate: 0,
        asyncSuccessRate: 0,
        avgSyncToAsyncFallbackTime: 0
      },
      apiUsageStats: {
        totalApiCalls: 0,
        successfulApiCalls: 0,
        failedApiCalls: 0,
        totalTokensUsed: 0,
        estimatedCosts: 0,
        averageTokensPerRequest: 0,
        apiErrorDistribution: {},
        rateLimitHits: 0,
        avgApiResponseTime: 0
      },
      errorStats: {
        totalErrors: 0,
        errorsByType: {},
        errorsByCategory: {},
        recentErrors: [],
        errorRate: 0,
        mtbf: 0
      },
      healthCheckHistory: [],
      uptimeStats: {
        serviceStartTime: Date.now(),
        totalUptimeMs: 0,
        totalDowntimeMs: 0,
        uptimePercentage: 100,
        lastHealthyTimestamp: Date.now(),
        healthCheckCount: 0,
        consecutiveHealthyChecks: 0,
        consecutiveUnhealthyChecks: 0
      }
    };
    
    // Set logger level
    this.logger.setLogLevel(this.config.logLevel);
    
    this.logger.info('PromptClassificationService initialized', 'Constructor', {
      syncTimeoutMs: this.config.syncTimeoutMs,
      enableAsyncFallback: this.config.enableAsyncFallback,
      maxConcurrentAsync: this.config.maxConcurrentAsync,
      hasApiService: !!this.mistralApiService,
      hasCategoryLoader: !!this.categoryLoader
    });
    
    // Initialize classification logger context
    const initCorrelationId = LoggingContext.create();
    this.classificationLogger.info(
      ClassificationEventType.SERVICE_INITIALIZED,
      'PromptClassificationService initialized successfully',
      initCorrelationId,
      {
        serviceType: 'PromptClassificationService',
        config: {
          syncTimeoutMs: this.config.syncTimeoutMs,
          maxRetries: this.config.maxRetries,
          enableAsyncFallback: this.config.enableAsyncFallback,
          maxConcurrentAsync: this.config.maxConcurrentAsync,
          defaultCategoryId: this.config.defaultCategoryId,
          enableMetrics: this.config.enableMetrics
        },
        dependencies: {
          hasApiService: !!this.mistralApiService,
          hasCategoryLoader: !!this.categoryLoader,
          hasPromptEngineering: !!this.promptEngineeringService
        }
      }
    );
    LoggingContext.cleanup(initCorrelationId);
    
    // Start async processing loop
    this.startAsyncProcessor();
    
    // Start performance alerting monitoring
    this.initializePerformanceAlerting();
  }

  /**
   * Main entry point for prompt classification
   * @param promptId Database ID of the prompt
   * @param promptText The prompt text to classify
   * @param options Additional classification options
   * @returns Classification result
   */
  public async classifyPrompt(
    promptId: string, 
    promptText: string,
    options: Partial<ClassificationPromptRequest> = {}
  ): Promise<ClassificationResult> {
    const requestId = this.generateRequestId();
    
    // Comprehensive input validation
    try {
      this.validateClassificationInput(promptId, promptText, options);
    } catch (validationError) {
      this.logger.error('Input validation failed', 'classifyPrompt', {
        requestId,
        promptId,
        error: validationError instanceof Error ? validationError.message : String(validationError)
      }, validationError instanceof Error ? validationError : undefined);

      // Create failed result for validation errors
      const result: ClassificationResult = {
        context: {
          requestId,
          promptId,
          promptText,
          processingMode: ProcessingMode.FAILED,
          startTime: this.performanceNow(),
          attempts: 0,
          status: ClassificationStatus.FAILED,
          error: validationError instanceof Error ? validationError : new Error(String(validationError))
        },
        success: false,
        categoryId: this.config.defaultCategoryId,
        categoryName: this.getDefaultCategoryName(),
        processingTimeMs: 0,
        processingMode: ProcessingMode.FAILED,
        error: validationError instanceof Error ? validationError : new Error(String(validationError))
      };

      this.recordError(validationError instanceof Error ? validationError : new Error(String(validationError)), 'input_validation');
      this.updateMetricsForResult(result);
      
      return result;
    }

    // Create classification context
    const context: ClassificationContext = {
      requestId,
      promptId,
      promptText,
      processingMode: ProcessingMode.SYNC,
      startTime: this.performanceNow(),
      attempts: 0,
      status: ClassificationStatus.PENDING,
      metadata: { ...options }
    };
    
    this.logger.debug('Starting prompt classification', 'classifyPrompt', {
      requestId: context.requestId,
      promptId: context.promptId,
      promptLength: promptText.length,
      processingMode: context.processingMode
    });
    
    // Update metrics
    this.metrics.totalRequests++;

    // Check service health before processing
    try {
      this.validateServiceHealth();
    } catch (healthError) {
      this.logger.warn('Service health check failed, proceeding with degraded mode', 'classifyPrompt', {
        requestId: context.requestId,
        error: healthError instanceof Error ? healthError.message : String(healthError)
      });
    }
    
    // Notify monitoring hooks
    this.monitoringHooks.forEach(hook => hook.onClassificationStart?.(context));
    
    try {
      // Attempt synchronous classification
      const result = await this.attemptSyncClassification(context, options);
      
      // Update metrics and return result
      this.updateMetricsForResult(result);
      this.monitoringHooks.forEach(hook => hook.onClassificationComplete?.(result));
      
      return result;
    } catch (error) {
      this.logger.error('Classification failed', 'classifyPrompt', { 
        requestId: context.requestId,
        error: error instanceof Error ? error.message : String(error)
      }, error instanceof Error ? error : undefined);

      // Try graceful degradation
      const processedError = error instanceof Error ? error : new Error(String(error));
      const degradationResult = await this.handleGracefulDegradation(processedError, context);
      
      if (degradationResult) {
        this.updateMetricsForResult(degradationResult);
        this.monitoringHooks.forEach(hook => hook.onClassificationComplete?.(degradationResult));
        return degradationResult;
      }
      
      // Create failure result as last resort
      const failureResult: ClassificationResult = {
        context: { ...context, status: ClassificationStatus.FAILED, error: processedError },
        success: false,
        categoryId: this.config.defaultCategoryId,
        categoryName: this.getDefaultCategoryName(),
        processingTimeMs: this.performanceNow() - context.startTime,
        processingMode: ProcessingMode.FAILED,
        error: processedError
      };
      
      this.recordError(processedError, 'classification_failed');
      this.updateMetricsForResult(failureResult);
      this.monitoringHooks.forEach(hook => hook.onClassificationComplete?.(failureResult));
      
      return failureResult;
    }
  }

  /**
   * Add a monitoring hook for observing operations
   * @param hook Monitoring hook implementation
   */
  public addMonitoringHook(hook: ClassificationMonitoringHook): void {
    this.monitoringHooks.push(hook);
  }

  /**
   * Remove a monitoring hook
   * @param hook Monitoring hook to remove
   */
  public removeMonitoringHook(hook: ClassificationMonitoringHook): void {
    const index = this.monitoringHooks.indexOf(hook);
    if (index !== -1) {
      this.monitoringHooks.splice(index, 1);
    }
  }

  /**
   * Get current service metrics
   * @returns Current metrics snapshot
   */
  public getMetrics(): ClassificationServiceMetrics {
    return {
      ...this.metrics,
      asyncQueueSize: this.asyncQueue.length
    };
  }

  /**
   * Get service configuration
   * @returns Current configuration
   */
  public getConfig(): ClassificationServiceConfig {
    return { ...this.config };
  }

  /**
   * Perform health check on the service
   * @returns Health status and diagnostics
   */
  public async performHealthCheck(): Promise<{
    isHealthy: boolean;
    status: string;
    diagnostics: Record<string, any>;
  }> {
    const diagnostics: Record<string, any> = {};
    let isHealthy = true;
    let status = 'healthy';
    
    try {
      // Check category loader health
      const categoryDiagnostics = this.categoryLoader.getDiagnostics();
      diagnostics.categoryLoader = categoryDiagnostics;
      if (!categoryDiagnostics.health.isHealthy) {
        isHealthy = false;
        status = 'degraded';
      }
      
      // Check Mistral API service health
      const mistralHealth = this.mistralApiService.getHealth();
      diagnostics.mistralApiService = mistralHealth;
      if (!mistralHealth.isHealthy) {
        isHealthy = false;
        status = 'degraded';
      }
      
      // Check async queue health
      diagnostics.asyncQueue = {
        queueSize: this.asyncQueue.length,
        currentOperations: this.currentAsyncOperations,
        maxConcurrent: this.config.maxConcurrentAsync
      };
      
      if (this.asyncQueue.length > 50) {
        isHealthy = false;
        status = 'overloaded';
      }
      
      // Check database connectivity
      try {
        const { error } = await this.supabaseClient
          .from('stored_prompts')
          .select('id')
          .limit(1);
        
        diagnostics.database = { connected: !error, error: error?.message };
        if (error) {
          isHealthy = false;
          status = 'database_error';
        }
      } catch (dbError) {
        diagnostics.database = { connected: false, error: dbError instanceof Error ? dbError.message : String(dbError) };
        isHealthy = false;
        status = 'database_error';
      }
      
      // Update service health
      this.metrics.isHealthy = isHealthy;
      
      return { isHealthy, status, diagnostics };
    } catch (error) {
      return {
        isHealthy: false,
        status: 'error',
        diagnostics: { error: error instanceof Error ? error.message : String(error) }
      };
    }
  }

  /**
   * Graceful shutdown of the service
   */
  public async shutdown(): Promise<void> {
    this.logger.info('Shutting down PromptClassificationService', 'shutdown');
    this.isShuttingDown = true;
    
    // Wait for current async operations to complete
    const maxWaitTime = 30000; // 30 seconds
    const startTime = Date.now();
    
    while (this.currentAsyncOperations > 0 && (Date.now() - startTime) < maxWaitTime) {
      await this.sleep(100);
    }
    
    this.logger.info('PromptClassificationService shutdown complete', 'shutdown', {
      remainingAsyncOperations: this.currentAsyncOperations,
      queueSize: this.asyncQueue.length
    });
  }

  /**
   * Attempt synchronous classification with timeout monitoring
   * @param context Classification context
   * @param options Additional classification options
   * @returns Classification result
   */
  private async attemptSyncClassification(
    context: ClassificationContext,
    options: Partial<ClassificationPromptRequest>
  ): Promise<ClassificationResult> {
    // Get processing recommendation from optimizer
    const complexityFactors: RequestComplexityFactors = {
      promptLength: context.promptText.length,
      categoryCount: this.categoryLoader.getCategories().length,
      processingDifficulty: this.estimateProcessingDifficulty(context.promptText),
      historicalPerformance: this.getHistoricalPerformance(context.promptText)
    };

    const processingRecommendation = await processingOptimizer.getProcessingRecommendation(complexityFactors);

    this.logger.debug('Received processing recommendation', 'attemptSyncClassification', {
      requestId: context.requestId,
      recommendedMode: processingRecommendation.mode,
      confidence: processingRecommendation.confidence,
      reasoning: processingRecommendation.reasoning,
      estimatedTime: processingRecommendation.estimatedProcessingTime
    });

    // Handle different processing mode recommendations
    if (processingRecommendation.mode === OptimizerMode.REJECTED) {
      throw new ClassificationServiceUnavailableError(processingRecommendation.reasoning, context.requestId);
    }

    if (processingRecommendation.mode === OptimizerMode.DEFERRED) {
      // Queue for deferred processing with longer delay
      this.queueAsyncClassification(context, options, true, processingRecommendation);
      return {
        context: { ...context, status: ClassificationStatus.PROCESSING, processingMode: ProcessingMode.ASYNC },
        success: false,
        categoryId: this.config.defaultCategoryId,
        categoryName: this.getDefaultCategoryName(),
        processingTimeMs: this.performanceNow() - context.startTime,
        processingMode: ProcessingMode.ASYNC,
        error: new Error('Request deferred due to system load')
      };
    }

    if (processingRecommendation.mode === OptimizerMode.ASYNC) {
      // Optimizer recommends async processing
      this.queueAsyncClassification(context, options, false, processingRecommendation);
      this.monitoringHooks.forEach(hook => hook.onAsyncModeSwitch?.(context));
      
      return {
        context: { ...context, status: ClassificationStatus.PROCESSING, processingMode: ProcessingMode.ASYNC },
        success: false,
        categoryId: this.config.defaultCategoryId,
        categoryName: this.getDefaultCategoryName(),
        processingTimeMs: this.performanceNow() - context.startTime,
        processingMode: ProcessingMode.ASYNC,
        error: new Error('Automatically switched to async processing')
      };
    }

    // Proceed with synchronous processing (SYNC mode)
    const timeoutMs = Math.min(processingRecommendation.estimatedProcessingTime * 1.2, this.getAdaptiveTimeout());
    
    this.logger.debug('Starting optimized synchronous classification', 'attemptSyncClassification', {
      requestId: context.requestId,
      promptId: context.promptId,
      timeoutMs,
      optimizerTimeoutMs: processingRecommendation.estimatedProcessingTime,
      adaptiveTimeoutMs: this.getAdaptiveTimeout(),
      confidence: processingRecommendation.confidence
    });

    // Update context to processing
    context.status = ClassificationStatus.PROCESSING;
    context.processingMode = ProcessingMode.SYNC;

    try {
      // Create classification request
      const classificationRequest: ClassificationPromptRequest = {
        promptText: context.promptText,
        ...options
      };

      // Build classification prompt
      const classificationPrompt = this.promptEngineeringService.generateClassificationPrompts(classificationRequest);
      
      this.logger.debug('Built classification prompt', 'attemptSyncClassification', {
        requestId: context.requestId,
        promptLength: classificationPrompt.systemPrompt.length + classificationPrompt.userPrompt.length
      });

      // Create timeout promise using adaptive timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new ClassificationTimeoutError(timeoutMs, context.requestId));
        }, timeoutMs);
      });

      // Create classification promise
      const classificationPromise = this.performMistralClassification(context, classificationPrompt);

      // Race between classification and timeout
      const apiResponse = await Promise.race([classificationPromise, timeoutPromise]);

      // Parse the classification response
      const parsedResult = this.promptEngineeringService.parseClassificationResponse(apiResponse.content || '');

      this.logger.debug('Parsed classification result', 'attemptSyncClassification', {
        requestId: context.requestId,
        categoryId: parsedResult.categoryId,
        confidence: parsedResult.confidence
      });

      // Validate the result
      const validatedResult = this.validateClassificationResult(context, parsedResult);

      // Update database with classification result
      await this.updateDatabaseWithClassification(context, validatedResult);

      // Calculate processing time
      const processingTimeMs = this.performanceNow() - context.startTime;
      this.recordSyncProcessingTime(processingTimeMs);

      // Create successful result
      const result: ClassificationResult = {
        context: {
          ...context,
          status: ClassificationStatus.COMPLETED,
          categoryId: validatedResult.categoryId,
          categoryName: validatedResult.categoryName
        },
        success: true,
        categoryId: validatedResult.categoryId,
        categoryName: validatedResult.categoryName,
        processingTimeMs,
        processingMode: ProcessingMode.SYNC,
        confidence: validatedResult.confidence
      };

      this.logger.info('Synchronous classification completed', 'attemptSyncClassification', {
        requestId: context.requestId,
        categoryId: validatedResult.categoryId,
        categoryName: validatedResult.categoryName,
        processingTimeMs,
        confidence: validatedResult.confidence
      });

      // Record processing result with optimizer for learning
      processingOptimizer.recordProcessingResult(processingRecommendation, {
        success: true,
        actualProcessingTime: processingTimeMs,
        actualMode: OptimizerMode.SYNC
      });

      return result;

    } catch (error) {
      const processingTimeMs = this.performanceNow() - context.startTime;

      if (error instanceof ClassificationTimeoutError) {
        this.logger.warn('Synchronous classification timed out, switching to async', 'attemptSyncClassification', {
          requestId: context.requestId,
          timeoutMs,
          processingTimeMs
        });

        // Record timeout result with optimizer
        processingOptimizer.recordProcessingResult(processingRecommendation, {
          success: false,
          actualProcessingTime: processingTimeMs,
          actualMode: OptimizerMode.SYNC
        });

        // If async fallback is enabled, queue for async processing
        if (this.config.enableAsyncFallback) {
          this.queueAsyncClassification(context, options);
          this.monitoringHooks.forEach(hook => hook.onAsyncModeSwitch?.(context));
          
          // Return intermediate result indicating async processing
          return {
            context: { ...context, status: ClassificationStatus.PROCESSING, processingMode: ProcessingMode.ASYNC },
            success: false,
            categoryId: this.config.defaultCategoryId,
            categoryName: this.getDefaultCategoryName(),
            processingTimeMs,
            processingMode: ProcessingMode.ASYNC,
            error
          };
        } else {
          // Async fallback disabled, treat as failure
          throw error;
        }
      }

      // Handle other errors
      this.logger.error('Synchronous classification failed', 'attemptSyncClassification', {
        requestId: context.requestId,
        error: error instanceof Error ? error.message : String(error),
        processingTimeMs
      }, error instanceof Error ? error : undefined);

      // Record failure result with optimizer
      processingOptimizer.recordProcessingResult(processingRecommendation, {
        success: false,
        actualProcessingTime: processingTimeMs,
        actualMode: OptimizerMode.SYNC
      });

      // Update context with error
      context.status = ClassificationStatus.FAILED;
      context.error = error instanceof Error ? error : new Error(String(error));

      // Notify monitoring hooks
      this.monitoringHooks.forEach(hook => hook.onClassificationFailure?.(context, context.error!));

      throw error;
    }
  }

  /**
   * Start the asynchronous processing loop
   */
  private startAsyncProcessor(): void {
    this.logger.debug('Starting async processor', 'startAsyncProcessor', {
      maxConcurrentAsync: this.config.maxConcurrentAsync
    });

    // Process queue periodically
    this.processAsyncQueue();
    
    // Set up recurring queue processing
    setInterval(() => {
      if (!this.isShuttingDown) {
        this.processAsyncQueue();
      }
    }, 1000); // Process queue every second
  }

  /**
   * Process pending items in the async queue
   */
  private async processAsyncQueue(): Promise<void> {
    // Don't process if we're at capacity or shutting down
    if (this.currentAsyncOperations >= this.config.maxConcurrentAsync || this.isShuttingDown) {
      return;
    }

    // Get tasks ready for processing
    const readyTasks = this.asyncQueue.filter(task => 
      Date.now() >= task.scheduledTime
    );

    if (readyTasks.length === 0) {
      return;
    }

    this.logger.debug('Processing async queue', 'processAsyncQueue', {
      queueSize: this.asyncQueue.length,
      readyTasks: readyTasks.length,
      currentOperations: this.currentAsyncOperations,
      maxConcurrent: this.config.maxConcurrentAsync
    });

    // Process tasks up to our concurrency limit
    const tasksToProcess = readyTasks.slice(0, this.config.maxConcurrentAsync - this.currentAsyncOperations);
    
    for (const task of tasksToProcess) {
      // Remove from queue
      const taskIndex = this.asyncQueue.indexOf(task);
      if (taskIndex !== -1) {
        this.asyncQueue.splice(taskIndex, 1);
      }

      // Process task in background
      this.processAsyncTask(task);
    }
  }

  /**
   * Process a single async classification task
   * @param task The async task to process
   */
  private async processAsyncTask(task: AsyncClassificationTask): Promise<void> {
    this.currentAsyncOperations++;
    
    const { context } = task;
    const taskStartTime = this.performanceNow();

    this.logger.debug('Starting async task processing', 'processAsyncTask', {
      requestId: context.requestId,
      retryAttempt: task.retryAttempt,
      currentOperations: this.currentAsyncOperations
    });

    try {
      // Update context for async processing
      context.status = ClassificationStatus.PROCESSING;
      context.processingMode = ProcessingMode.ASYNC;
      context.attempts = task.retryAttempt + 1;

      // Create classification request (same as sync flow)
      const classificationRequest: ClassificationPromptRequest = {
        promptText: context.promptText,
        ...context.metadata
      };

      // Build classification prompt
      const classificationPrompt = this.promptEngineeringService.generateClassificationPrompts(classificationRequest);
      
      this.logger.debug('Built async classification prompt', 'processAsyncTask', {
        requestId: context.requestId,
        promptLength: classificationPrompt.systemPrompt.length + classificationPrompt.userPrompt.length
      });

      // Perform classification (no timeout for async)
      const apiResponse = await this.performMistralClassification(context, classificationPrompt);

      // Parse the classification response
      const parsedResult = this.promptEngineeringService.parseClassificationResponse(apiResponse.content || '');

      this.logger.debug('Parsed async classification result', 'processAsyncTask', {
        requestId: context.requestId,
        categoryId: parsedResult.categoryId,
        confidence: parsedResult.confidence
      });

      // Validate the result
      const validatedResult = this.validateClassificationResult(context, parsedResult);

      // Update database with classification result
      await this.updateDatabaseWithClassification(context, validatedResult);

      // Calculate processing time
      const processingTimeMs = this.performanceNow() - taskStartTime;
      this.recordAsyncProcessingTime(processingTimeMs);

      // Update context with success
      context.status = ClassificationStatus.COMPLETED;
      context.categoryId = validatedResult.categoryId;
      context.categoryName = validatedResult.categoryName;

      // Update metrics
      this.metrics.asyncModeSuccesses++;
      this.metrics.successfulClassifications++;
      this.updateCategoryDistribution(validatedResult.categoryId);

      this.logger.info('Async classification completed successfully', 'processAsyncTask', {
        requestId: context.requestId,
        categoryId: validatedResult.categoryId,
        categoryName: validatedResult.categoryName,
        processingTimeMs,
        confidence: validatedResult.confidence,
        retryAttempt: task.retryAttempt
      });

      // Record processing result with optimizer if recommendation is available
      if (task.processingRecommendation) {
        processingOptimizer.recordProcessingResult(task.processingRecommendation, {
          success: true,
          actualProcessingTime: processingTimeMs,
          actualMode: OptimizerMode.ASYNC
        });
      }

      // Create successful result for monitoring hooks
      const result: ClassificationResult = {
        context,
        success: true,
        categoryId: validatedResult.categoryId,
        categoryName: validatedResult.categoryName,
        processingTimeMs,
        processingMode: ProcessingMode.ASYNC,
        confidence: validatedResult.confidence
      };

      // Notify monitoring hooks
      this.monitoringHooks.forEach(hook => hook.onClassificationComplete?.(result));

    } catch (error) {
      const processingTimeMs = this.performanceNow() - taskStartTime;
      
      this.logger.error('Async classification failed', 'processAsyncTask', {
        requestId: context.requestId,
        error: error instanceof Error ? error.message : String(error),
        retryAttempt: task.retryAttempt,
        processingTimeMs
      }, error instanceof Error ? error : undefined);

      // Update context with error
      context.error = error instanceof Error ? error : new Error(String(error));
      context.status = ClassificationStatus.FAILED;

      // Record processing result with optimizer if recommendation is available
      if (task.processingRecommendation) {
        processingOptimizer.recordProcessingResult(task.processingRecommendation, {
          success: false,
          actualProcessingTime: processingTimeMs,
          actualMode: OptimizerMode.ASYNC
        });
      }

      // Determine if we should retry
      if (this.shouldRetryAsyncTask(task, error)) {
        await this.scheduleAsyncRetry(task, error);
      } else {
        // Final failure - use default category
        await this.handleFinalAsyncFailure(context, error);
      }

      // Notify monitoring hooks of failure
      this.monitoringHooks.forEach(hook => hook.onClassificationFailure?.(context, context.error!));

    } finally {
      this.currentAsyncOperations--;
      
      this.logger.debug('Async task processing completed', 'processAsyncTask', {
        requestId: context.requestId,
        currentOperations: this.currentAsyncOperations,
        queueSize: this.asyncQueue.length
      });
    }
  }

  /**
   * Determine if an async task should be retried
   * @param task The failed task
   * @param error The error that occurred
   * @returns Whether to retry the task
   */
  private shouldRetryAsyncTask(task: AsyncClassificationTask, error: any): boolean {
    // Don't retry if we've exceeded max attempts
    if (task.retryAttempt >= this.config.maxRetries) {
      return false;
    }

    // Don't retry if we're shutting down
    if (this.isShuttingDown) {
      return false;
    }

    // Retry for transient errors
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      
      // Retry for network/timeout errors
      if (errorMessage.includes('network') || 
          errorMessage.includes('timeout') || 
          errorMessage.includes('connection') ||
          errorMessage.includes('503') ||
          errorMessage.includes('502') ||
          errorMessage.includes('429')) {
        return true;
      }
    }

    // Don't retry for validation/parsing errors
    if (error instanceof ClassificationDatabaseError) {
      return false;
    }

    // Default to retry for unknown errors
    return true;
  }

  /**
   * Schedule a retry for a failed async task
   * @param task The failed task
   * @param error The error that occurred
   */
  private async scheduleAsyncRetry(task: AsyncClassificationTask, error: any): Promise<void> {
    const nextRetryAttempt = task.retryAttempt + 1;
    const delayMs = Math.min(
      this.config.retryDelayMultiplier ** nextRetryAttempt * 1000,
      this.config.maxRetryDelayMs
    );
    
    const retryTask: AsyncClassificationTask = {
      ...task,
      retryAttempt: nextRetryAttempt,
      scheduledTime: Date.now() + delayMs
    };

    this.asyncQueue.push(retryTask);
    this.metrics.totalRetries++;

    this.logger.warn('Scheduling async retry', 'scheduleAsyncRetry', {
      requestId: task.context.requestId,
      retryAttempt: nextRetryAttempt,
      delayMs,
      error: error instanceof Error ? error.message : String(error)
    });

    // Notify monitoring hooks
    this.monitoringHooks.forEach(hook => hook.onRetry?.(task.context, nextRetryAttempt));
  }

  /**
   * Handle final failure of an async task (no more retries)
   * @param context Task context
   * @param error Final error
   */
  private async handleFinalAsyncFailure(context: ClassificationContext, error: any): Promise<void> {
    this.logger.error('Async classification failed permanently', 'handleFinalAsyncFailure', {
      requestId: context.requestId,
      attempts: context.attempts,
      error: error instanceof Error ? error.message : String(error)
    });

    try {
      // Update database with default category
      const defaultCategory = this.categoryLoader.getCategoryById(this.config.defaultCategoryId);
      await this.supabaseClient
        .from('stored_prompts')
        .update({ prompt_category: defaultCategory?.name || 'Other' })
        .eq('id', context.promptId);

      // Update context
      context.status = ClassificationStatus.FAILED;
      context.categoryId = this.config.defaultCategoryId;
      context.categoryName = defaultCategory?.name || 'Other';

      // Update metrics
      this.metrics.failedClassifications++;
      this.updateCategoryDistribution(this.config.defaultCategoryId);

      this.logger.info('Applied default category for failed async classification', 'handleFinalAsyncFailure', {
        requestId: context.requestId,
        defaultCategoryId: this.config.defaultCategoryId,
        defaultCategoryName: context.categoryName
      });

    } catch (dbError) {
      this.logger.error('Failed to apply default category', 'handleFinalAsyncFailure', {
        requestId: context.requestId,
        dbError: dbError instanceof Error ? dbError.message : String(dbError)
      });
    }
  }

  /**
   * Record async processing time for metrics
   * @param timeMs Processing time in milliseconds
   */
  private recordAsyncProcessingTime(timeMs: number): void {
    this.asyncProcessingTimes.push(timeMs);
    if (this.asyncProcessingTimes.length > this.maxTimingSamples) {
      this.asyncProcessingTimes.shift();
    }

    // Update average
    const sum = this.asyncProcessingTimes.reduce((a, b) => a + b, 0);
    this.metrics.averageAsyncTimeMs = sum / this.asyncProcessingTimes.length;
  }

  /**
   * Update category distribution metrics
   * @param categoryId Category ID to increment
   */
  private updateCategoryDistribution(categoryId: number): void {
    if (!this.metrics.categoryDistribution[categoryId]) {
      this.metrics.categoryDistribution[categoryId] = 0;
    }
    this.metrics.categoryDistribution[categoryId]++;
  }

  /**
   * Perform Mistral API classification request
   * @param context Classification context
   * @param prompt Classification prompt
   * @returns API response
   */
  private async performMistralClassification(
    context: ClassificationContext,
    prompt: ClassificationPromptResponse
  ): Promise<{ content: string }> {
    const request: ClassificationRequest = {
      promptText: context.promptText,
      availableCategories: prompt.availableCategories.map((cat: Category) => cat.name),
      context: context.metadata?.source || undefined
    };

    this.logger.debug('Sending request to Mistral API', 'performMistralClassification', {
      requestId: context.requestId,
      promptLength: context.promptText.length,
      categoriesCount: request.availableCategories.length
    });

    const apiStartTime = this.performanceNow();
    let apiResponseTime = 0;
    let success = false;
    let tokensUsed = 0;
    let apiError: Error | undefined;

    try {
      const response = await this.mistralApiService.classifyPrompt(request);
      
      apiResponseTime = this.performanceNow() - apiStartTime;
      success = true;
      
      // Estimate tokens used (rough approximation: 1 token = ~4 characters)
      tokensUsed = Math.ceil((context.promptText.length + JSON.stringify(request.availableCategories).length) / 4);
      
      this.logger.debug('Received response from Mistral API', 'performMistralClassification', {
        requestId: context.requestId,
        responseTimeMs: apiResponseTime.toFixed(2),
        estimatedTokens: tokensUsed,
        hasResponse: !!response.rawResponse
      });
      
      return {
        content: response.rawResponse
      };
    } catch (error) {
      apiResponseTime = this.performanceNow() - apiStartTime;
      success = false;
      apiError = error instanceof Error ? error : new Error(String(error));
      
      // Enhanced error processing
      const enhancedError = this.enhanceApiError(error, 'mistral_classification');
      
      this.logger.error('Mistral API request failed', 'performMistralClassification', {
        requestId: context.requestId,
        responseTimeMs: apiResponseTime.toFixed(2),
        errorType: enhancedError.constructor.name,
        errorCode: (enhancedError as any).code
      }, enhancedError);
      
      throw enhancedError;
    } finally {
      // Record API usage statistics
      this.recordApiUsage(apiResponseTime, success, tokensUsed, apiError);
    }
  }

  /**
   * Validate classification result and ensure it's within valid range
   * @param context Classification context
   * @param parsedResult Parsed classification result
   * @returns Validated result with category name
   */
  private validateClassificationResult(
    context: ClassificationContext,
    parsedResult: ParsedClassificationResult
  ): ParsedClassificationResult & { categoryName: string } {
    const category = this.categoryLoader.getCategoryById(parsedResult.categoryId);
    
    if (!category) {
      this.logger.warn('Invalid category ID returned, using default', 'validateClassificationResult', {
        requestId: context.requestId,
        invalidCategoryId: parsedResult.categoryId,
        defaultCategoryId: this.config.defaultCategoryId
      });
      
      const defaultCategory = this.categoryLoader.getCategoryById(this.config.defaultCategoryId);
      return {
        categoryId: this.config.defaultCategoryId,
        categoryName: defaultCategory?.name || 'Other',
        confidence: 0.5,
        rawResponse: parsedResult.rawResponse,
        parsingMethod: parsedResult.parsingMethod
      };
    }

    return {
      ...parsedResult,
      categoryName: category.name
    };
  }

  /**
   * Update database with classification result
   * @param context Classification context
   * @param result Validated classification result
   */
  private async updateDatabaseWithClassification(
    context: ClassificationContext,
    result: ParsedClassificationResult & { categoryName: string }
  ): Promise<void> {
    try {
      this.logger.debug('🔍 Starting database update for classification', 'updateDatabaseWithClassification', {
        requestId: context.requestId,
        promptId: context.promptId,
        categoryId: result.categoryId,
        categoryName: result.categoryName
      });

      // First, verify the prompt exists in the database
      this.databaseLogger.debug('Checking if prompt exists in database', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        step: 'verify_prompt_exists'
      });
      const { data: existingPrompt, error: selectError } = await this.supabaseClient
        .from('stored_prompts')
        .select('id, prompt, prompt_category, created_at')
        .eq('id', context.promptId)
        .single();

      if (selectError) {
        this.databaseLogger.error('Failed to find prompt in database', {
          operation: 'updateDatabaseWithClassification',
          requestId: context.requestId,
          promptId: context.promptId,
          error: {
            message: selectError.message,
            code: selectError.code,
            details: selectError.details
          },
          step: 'verify_prompt_exists'
        });
        this.logger.error('Failed to find prompt in database before update', 'updateDatabaseWithClassification', {
          requestId: context.requestId,
          promptId: context.promptId,
          selectError: selectError.message,
          selectErrorCode: selectError.code,
          selectErrorDetails: selectError.details
        });
        throw new ClassificationDatabaseError(
          `Prompt not found before update: ${selectError.message}`,
          context.requestId
        );
      }

      this.databaseLogger.info('Found prompt in database, proceeding with classification update', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: existingPrompt.id,
        currentCategory: existingPrompt.prompt_category,
        newCategory: result.categoryName,
        promptPreview: existingPrompt.prompt?.substring(0, 100) + '...',
        step: 'prompt_found'
      });

      this.logger.info('Found prompt in database, proceeding with update', 'updateDatabaseWithClassification', {
        requestId: context.requestId,
        promptId: context.promptId,
        existingCategory: existingPrompt.prompt_category,
        newCategory: result.categoryName,
        promptText: existingPrompt.prompt?.substring(0, 100) + '...'
      });

      // Perform the update
      this.databaseLogger.debug('Starting database update for prompt category', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        fromCategory: existingPrompt.prompt_category,
        toCategory: result.categoryName,
        step: 'execute_update',
        query: `UPDATE stored_prompts SET prompt_category = '${result.categoryName}' WHERE id = '${context.promptId}'`
      });
      
      const { data, error } = await this.supabaseClient
        .from('stored_prompts')
        .update({ prompt_category: result.categoryName })
        .eq('id', context.promptId)
        .select('id, prompt_category');

      this.databaseLogger.debug('Database update query response received', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        hasData: !!data,
        dataLength: data?.length || 0,
        hasError: !!error,
        step: 'query_response',
        dataContent: data
      });

      if (error) {
        this.databaseLogger.error('Database update query failed', {
          operation: 'updateDatabaseWithClassification',
          requestId: context.requestId,
          promptId: context.promptId,
          error: {
            message: error.message,
            code: error.code,
            details: error.details
          },
          originalCategory: existingPrompt.prompt_category,
          targetCategory: result.categoryName,
          step: 'query_error'
        });
        this.logger.error('Database update query failed', 'updateDatabaseWithClassification', {
          requestId: context.requestId,
          promptId: context.promptId,
          updateError: error.message,
          updateCode: error.code,
          updateDetails: error.details,
          originalCategory: existingPrompt.prompt_category,
          targetCategory: result.categoryName
        });
        throw new ClassificationDatabaseError(
          `Failed to update prompt category: ${error.message}`,
          context.requestId
        );
      }

      // Check if the update actually affected any rows by looking at returned data
      if (!data || data.length === 0) {
        this.databaseLogger.warn('Database update affected 0 rows - possible RLS policy issue', {
          operation: 'updateDatabaseWithClassification',
          requestId: context.requestId,
          promptId: context.promptId,
          dataLength: data?.length || 0,
          originalCategory: existingPrompt.prompt_category,
          targetCategory: result.categoryName,
          step: 'zero_rows_affected',
          possibleCause: 'Row Level Security policy or permissions preventing update'
        });
        this.logger.error('Database update affected 0 rows', 'updateDatabaseWithClassification', {
          requestId: context.requestId,
          promptId: context.promptId,
          dataLength: data?.length || 0,
          originalCategory: existingPrompt.prompt_category,
          targetCategory: result.categoryName,
          possibleCause: 'Row Level Security policy or permissions preventing update'
        });
      }

      this.databaseLogger.debug('Database update query completed', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        rowsAffected: data?.length || 0,
        returnedData: data,
        step: 'update_completed'
      });

      this.logger.info('Database updated successfully', 'updateDatabaseWithClassification', {
        requestId: context.requestId,
        promptId: context.promptId,
        updatedCategory: result.categoryName,
        updatedRows: data?.length || 0,
        verificationData: data
      });

      // Double-check the update worked by querying again
      this.databaseLogger.debug('Starting verification of database update', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        step: 'verify_update'
      });
      const { data: finalVerification, error: verifyError } = await this.supabaseClient
        .from('stored_prompts')
        .select('id, prompt_category')
        .eq('id', context.promptId)
        .single();

      if (verifyError) {
        this.databaseLogger.warn('Failed to verify database update', {
          operation: 'updateDatabaseWithClassification',
          requestId: context.requestId,
          promptId: context.promptId,
          error: {
            message: verifyError.message,
            code: verifyError.code
          },
          step: 'verify_error'
        });
        this.logger.warn('Failed to verify database update', 'updateDatabaseWithClassification', {
          requestId: context.requestId,
          promptId: context.promptId,
          verifyError: verifyError.message
        });
      } else {
        const updateSuccessful = finalVerification.prompt_category === result.categoryName;
        this.databaseLogger.info('Database update verification completed', {
          operation: 'updateDatabaseWithClassification',
          requestId: context.requestId,
          promptId: context.promptId,
          expectedCategory: result.categoryName,
          actualCategory: finalVerification.prompt_category,
          updateSuccessful: updateSuccessful,
          step: 'verification_complete'
        });

        this.logger.info('Final verification of database update', 'updateDatabaseWithClassification', {
          requestId: context.requestId,
          promptId: context.promptId,
          finalCategory: finalVerification.prompt_category,
          expectedCategory: result.categoryName,
          updateSuccessful: updateSuccessful
        });

        if (!updateSuccessful) {
          this.databaseLogger.error('DATABASE UPDATE VERIFICATION FAILED!', {
            operation: 'updateDatabaseWithClassification',
            requestId: context.requestId,
            promptId: context.promptId,
            expected: result.categoryName,
            actual: finalVerification.prompt_category,
            message: 'The database update appeared to succeed but verification shows the value was not updated',
            step: 'verification_failed'
          });
          this.logger.error('DATABASE UPDATE VERIFICATION FAILED!', 'updateDatabaseWithClassification', {
            requestId: context.requestId,
            promptId: context.promptId,
            expected: result.categoryName,
            actual: finalVerification.prompt_category,
            message: 'The database update appeared to succeed but verification shows the value was not updated'
          });
        } else {
          this.databaseLogger.info('Database update confirmed successful', {
            operation: 'updateDatabaseWithClassification',
            requestId: context.requestId,
            promptId: context.promptId,
            finalCategory: finalVerification.prompt_category,
            step: 'verification_success'
          });
        }
      }

    } catch (error) {
      this.databaseLogger.error('Database update failed with unexpected error', {
        operation: 'updateDatabaseWithClassification',
        requestId: context.requestId,
        promptId: context.promptId,
        error: {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        },
        step: 'unexpected_error'
      });
      // Enhanced database error processing
      const enhancedError = this.enhanceDatabaseError(error, `update_classification_${context.requestId}`);
      
      this.logger.error('Database update failed', 'updateDatabaseWithClassification', {
        requestId: context.requestId,
        promptId: context.promptId,
        error: enhancedError.message,
        errorType: enhancedError.constructor.name
      }, enhancedError);

      throw enhancedError;
    }
  }

  /**
   * Record synchronous processing time for metrics
   * @param timeMs Processing time in milliseconds
   */
  private recordSyncProcessingTime(timeMs: number): void {
    this.syncProcessingTimes.push(timeMs);
    if (this.syncProcessingTimes.length > this.maxTimingSamples) {
      this.syncProcessingTimes.shift();
    }
  }

  /**
   * Queue classification for asynchronous processing
   * @param context Classification context
   * @param options Classification options
   * @param deferred Whether this is deferred processing (with longer delay)
   * @param processingRecommendation Optional processing recommendation to track
   */
  private queueAsyncClassification(
    context: ClassificationContext,
    options: Partial<ClassificationPromptRequest>,
    deferred: boolean = false,
    processingRecommendation?: ProcessingRecommendation
  ): void {
    const delay = deferred ? 30000 : 0; // 30 second delay for deferred processing
    const task: AsyncClassificationTask = {
      context: { ...context, processingMode: ProcessingMode.ASYNC },
      retryAttempt: 0,
      scheduledTime: Date.now() + delay,
      processingRecommendation
    };

    this.asyncQueue.push(task);
    
    this.logger.debug('Queued task for async processing', 'queueAsyncClassification', {
      requestId: context.requestId,
      queueSize: this.asyncQueue.length,
      deferred,
      delayMs: delay,
      hasRecommendation: !!processingRecommendation
    });
  }

  /**
   * Calculate latency percentiles from timing samples
   * @param samples Array of timing samples
   * @returns Calculated percentiles
   */
  private calculateLatencyPercentiles(samples: number[]): LatencyPercentiles {
    if (samples.length === 0) {
      return { p50: 0, p75: 0, p90: 0, p95: 0, p99: 0, min: 0, max: 0 };
    }

    const sorted = [...samples].sort((a, b) => a - b);
    const length = sorted.length;

    return {
      p50: this.getPercentile(sorted, 0.5),
      p75: this.getPercentile(sorted, 0.75),
      p90: this.getPercentile(sorted, 0.9),
      p95: this.getPercentile(sorted, 0.95),
      p99: this.getPercentile(sorted, 0.99),
      min: sorted[0],
      max: sorted[length - 1]
    };
  }

  /**
   * Get percentile value from sorted array
   * @param sortedArray Sorted array of values
   * @param percentile Percentile value (0.0 to 1.0)
   * @returns Percentile value
   */
  private getPercentile(sortedArray: number[], percentile: number): number {
    const index = percentile * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (upper >= sortedArray.length) return sortedArray[sortedArray.length - 1];
    if (lower === upper) return sortedArray[lower];

    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  }

  /**
   * Record API response time and update API usage statistics
   * @param responseTimeMs API response time in milliseconds
   * @param success Whether the API call was successful
   * @param tokensUsed Number of tokens used (optional)
   * @param error Error information if call failed
   */
  private recordApiUsage(
    responseTimeMs: number, 
    success: boolean, 
    tokensUsed?: number,
    error?: Error
  ): void {
    this.apiResponseTimes.push(responseTimeMs);
    if (this.apiResponseTimes.length > this.maxTimingSamples) {
      this.apiResponseTimes.shift();
    }

    // Update API usage stats
    this.metrics.apiUsageStats.totalApiCalls++;
    
    if (success) {
      this.metrics.apiUsageStats.successfulApiCalls++;
      if (tokensUsed) {
        this.metrics.apiUsageStats.totalTokensUsed += tokensUsed;
        this.metrics.apiUsageStats.averageTokensPerRequest = 
          this.metrics.apiUsageStats.totalTokensUsed / this.metrics.apiUsageStats.successfulApiCalls;
        
        // Estimate costs (approximate Mistral pricing: $0.25 per 1M tokens)
        this.metrics.apiUsageStats.estimatedCosts = 
          (this.metrics.apiUsageStats.totalTokensUsed / 1000000) * 0.25;
      }
    } else {
      this.metrics.apiUsageStats.failedApiCalls++;
      
      if (error) {
        const errorType = error.constructor.name;
        this.metrics.apiUsageStats.apiErrorDistribution[errorType] = 
          (this.metrics.apiUsageStats.apiErrorDistribution[errorType] || 0) + 1;
        
        // Check for rate limit hits
        if (error.message.toLowerCase().includes('429') || 
            error.message.toLowerCase().includes('rate limit')) {
          this.metrics.apiUsageStats.rateLimitHits++;
        }
      }
    }

    // Update average API response time
    const sum = this.apiResponseTimes.reduce((a, b) => a + b, 0);
    this.metrics.apiUsageStats.avgApiResponseTime = sum / this.apiResponseTimes.length;
  }

  /**
   * Record error for comprehensive error tracking
   * @param error Error that occurred
   * @param context Additional context information
   */
  private recordError(error: Error, context?: string): void {
    const errorEntry = {
      timestamp: Date.now(),
      type: error.constructor.name,
      message: error.message,
      context
    };

    this.errorHistory.push(errorEntry);
    if (this.errorHistory.length > this.maxErrorHistory) {
      this.errorHistory.shift();
    }

    // Update error statistics
    this.metrics.errorStats.totalErrors++;
    this.metrics.errorStats.errorsByType[error.constructor.name] = 
      (this.metrics.errorStats.errorsByType[error.constructor.name] || 0) + 1;
    
    if (context) {
      this.metrics.errorStats.errorsByCategory[context] = 
        (this.metrics.errorStats.errorsByCategory[context] || 0) + 1;
    }

    // Update recent errors (keep last 10)
    this.metrics.errorStats.recentErrors = this.errorHistory.slice(-10);

    // Calculate error rate (errors per hour)
    const hourAgo = Date.now() - (60 * 60 * 1000);
    const recentErrors = this.errorHistory.filter(e => e.timestamp > hourAgo);
    this.metrics.errorStats.errorRate = recentErrors.length;

    // Calculate MTBF (Mean Time Between Failures) in minutes
    if (this.errorHistory.length >= 2) {
      const timeBetweenErrors = this.errorHistory.slice(-2);
      const timeDiff = timeBetweenErrors[1].timestamp - timeBetweenErrors[0].timestamp;
      this.metrics.errorStats.mtbf = timeDiff / (1000 * 60); // Convert to minutes
    }
  }

  /**
   * Record sync-to-async fallback timing
   * @param fallbackTimeMs Time when fallback occurred
   */
  private recordSyncToAsyncFallback(fallbackTimeMs: number): void {
    this.syncToAsyncFallbackTimes.push(fallbackTimeMs);
    if (this.syncToAsyncFallbackTimes.length > this.maxTimingSamples) {
      this.syncToAsyncFallbackTimes.shift();
    }

    // Update processing mode distribution
    const sum = this.syncToAsyncFallbackTimes.reduce((a, b) => a + b, 0);
    this.metrics.processingModeDistribution.avgSyncToAsyncFallbackTime = 
      sum / this.syncToAsyncFallbackTimes.length;
  }

  /**
   * Update comprehensive processing mode statistics
   */
  private updateProcessingModeStats(): void {
    const stats = this.metrics.processingModeDistribution;
    
    // Update counts
    stats.syncCount = this.metrics.syncModeSuccesses;
    stats.asyncCount = this.metrics.asyncModeSuccesses;
    stats.failedCount = this.metrics.failedClassifications;

    // Calculate success rates
    const totalSync = this.metrics.syncModeSuccesses + 
      this.syncToAsyncFallbackTimes.length; // Sync attempts that timed out
    stats.syncSuccessRate = totalSync > 0 ? this.metrics.syncModeSuccesses / totalSync : 0;
    stats.asyncSuccessRate = stats.asyncCount > 0 ? 
      this.metrics.asyncModeSuccesses / (this.metrics.asyncModeSuccesses + stats.failedCount) : 0;
  }

  /**
   * Update uptime statistics
   * @param isHealthy Current health status
   */
  private updateUptimeStats(isHealthy: boolean): void {
    const now = Date.now();
    const stats = this.metrics.uptimeStats;
    
    stats.healthCheckCount++;
    
    if (isHealthy) {
      stats.consecutiveHealthyChecks++;
      stats.consecutiveUnhealthyChecks = 0;
      
      if (stats.lastUnhealthyTimestamp) {
        // We were unhealthy, now we're healthy
        stats.totalDowntimeMs += now - stats.lastUnhealthyTimestamp;
        stats.lastUnhealthyTimestamp = undefined;
      }
      stats.lastHealthyTimestamp = now;
    } else {
      stats.consecutiveUnhealthyChecks++;
      stats.consecutiveHealthyChecks = 0;
      
      if (!stats.lastUnhealthyTimestamp) {
        // We were healthy, now we're unhealthy
        if (stats.lastHealthyTimestamp) {
          stats.totalUptimeMs += now - stats.lastHealthyTimestamp;
        }
        stats.lastUnhealthyTimestamp = now;
      }
    }

    // Calculate uptime percentage
    const totalTime = now - stats.serviceStartTime;
    const actualUptime = isHealthy ? 
      stats.totalUptimeMs + (now - (stats.lastUnhealthyTimestamp || stats.lastHealthyTimestamp)) :
      stats.totalUptimeMs;
    
    stats.uptimePercentage = totalTime > 0 ? (actualUptime / totalTime) * 100 : 100;
  }

  /**
   * Update all advanced metrics
   */
  private updateAdvancedMetrics(): void {
    // Update latency percentiles
    this.metrics.syncLatencyPercentiles = this.calculateLatencyPercentiles(this.syncProcessingTimes);
    this.metrics.asyncLatencyPercentiles = this.calculateLatencyPercentiles(this.asyncProcessingTimes);
    
    // Update processing mode statistics
    this.updateProcessingModeStats();
    
    // Update uptime stats based on current health
    this.updateUptimeStats(this.metrics.isHealthy);
  }

  /**
   * Update metrics based on classification result
   * @param result Classification result to extract metrics from
   */
  private updateMetricsForResult(result: ClassificationResult): void {
    this.logger.debug('Updating metrics for classification result', 'updateMetricsForResult', {
      success: result.success,
      processingMode: result.processingMode,
      categoryId: result.categoryId,
      processingTimeMs: result.processingTimeMs
    });

    // Update success/failure counts
    if (result.success) {
      this.metrics.successfulClassifications++;
      
      // Update mode-specific success counts
      if (result.processingMode === ProcessingMode.SYNC) {
        this.metrics.syncModeSuccesses++;
      } else if (result.processingMode === ProcessingMode.ASYNC) {
        this.metrics.asyncModeSuccesses++;
      }
      
      // Update category distribution
      this.updateCategoryDistribution(result.categoryId);
    } else {
      this.metrics.failedClassifications++;
      
      // Record error for comprehensive tracking
      if (result.error) {
        this.recordError(result.error, `${result.processingMode}_classification`);
      }
      
      // Check for timeout errors
      if (result.error instanceof ClassificationTimeoutError) {
        this.metrics.timeouts++;
      }
    }

    // Update processing time metrics
    if (result.processingMode === ProcessingMode.SYNC) {
      this.recordSyncProcessingTime(result.processingTimeMs);
      
      // Update average sync processing time
      const sum = this.syncProcessingTimes.reduce((a, b) => a + b, 0);
      this.metrics.averageSyncTimeMs = this.syncProcessingTimes.length > 0 ? 
        sum / this.syncProcessingTimes.length : 0;
    } else if (result.processingMode === ProcessingMode.ASYNC) {
      this.recordAsyncProcessingTime(result.processingTimeMs);
      
      // Update average async processing time
      const sum = this.asyncProcessingTimes.reduce((a, b) => a + b, 0);
      this.metrics.averageAsyncTimeMs = this.asyncProcessingTimes.length > 0 ? 
        sum / this.asyncProcessingTimes.length : 0;
    }

    // Update all advanced metrics
    this.updateAdvancedMetrics();

    // Update service health evaluation
    this.updateServiceHealthStatus();

    this.logger.debug('Metrics update completed', 'updateMetricsForResult', {
      totalRequests: this.metrics.totalRequests,
      successfulClassifications: this.metrics.successfulClassifications,
      failedClassifications: this.metrics.failedClassifications,
      successRate: (this.metrics.totalRequests > 0 ? 
        this.metrics.successfulClassifications / this.metrics.totalRequests : 1.0).toFixed(3),
      isHealthy: this.metrics.isHealthy,
      avgSyncTimeMs: this.metrics.averageSyncTimeMs.toFixed(2),
      avgAsyncTimeMs: this.metrics.averageAsyncTimeMs.toFixed(2)
    });
  }

  /**
   * Update service health status based on comprehensive performance metrics
   */
  private updateServiceHealthStatus(): void {
    const successRate = this.metrics.totalRequests > 0 ? 
      this.metrics.successfulClassifications / this.metrics.totalRequests : 1.0;
    
    const recentErrorCount = this.errorHistory.filter(
      e => e.timestamp > Date.now() - (5 * 60 * 1000) // Last 5 minutes
    ).length;
    
    // Check API performance
    const avgApiResponseTime = this.metrics.apiUsageStats.avgApiResponseTime;
    const apiErrorRate = this.metrics.apiUsageStats.totalApiCalls > 0 ?
      this.metrics.apiUsageStats.failedApiCalls / this.metrics.apiUsageStats.totalApiCalls : 0;
    
    // Consider service healthy if:
    // - Success rate is above 70%
    // - Less than 10 errors in last 5 minutes
    // - API error rate is below 20%
    // - Average API response time is below 5 seconds
    // - No consecutive failures in recent operations
    const isHealthy = successRate >= 0.7 && 
                     recentErrorCount < 10 && 
                     apiErrorRate < 0.2 &&
                     (avgApiResponseTime === 0 || avgApiResponseTime < 5000) &&
                     this.metrics.uptimeStats.consecutiveUnhealthyChecks < 3;
    
    this.metrics.isHealthy = isHealthy;
    this.updateUptimeStats(isHealthy);
    
    // Store health check result in history
    const healthCheckResult: HealthCheckResult = {
      timestamp: Date.now(),
      isHealthy,
      status: isHealthy ? 'healthy' : 'degraded',
      responseTimeMs: this.performanceNow(),
      details: {
        successRate: successRate.toFixed(3),
        recentErrors: recentErrorCount,
        apiErrorRate: apiErrorRate.toFixed(3),
        avgApiResponseTime: avgApiResponseTime.toFixed(2),
        consecutiveUnhealthyChecks: this.metrics.uptimeStats.consecutiveUnhealthyChecks
      }
    };
    
    this.metrics.healthCheckHistory.push(healthCheckResult);
    if (this.metrics.healthCheckHistory.length > this.maxHealthCheckHistory) {
      this.metrics.healthCheckHistory.shift();
    }
    
    this.lastHealthCheck = healthCheckResult;
  }

  private getDefaultCategoryName(): string {
    const defaultCategory = this.categoryLoader.getCategoryById(this.config.defaultCategoryId);
    return defaultCategory?.name || 'Other';
  }

  private generateRequestId(): string {
    return `class-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Initialize performance alerting with custom notification handlers
   */
  private initializePerformanceAlerting(): void {
    // Capture logger reference for use in callbacks
    const databaseLogger = this.databaseLogger;
    
    // Set up custom alert notification handler for classification-specific alerts
    const classificationAlertHandler: AlertNotificationHandler = {
      name: 'classification-service',
      enabled: true,
      async handleAlert(alert: PerformanceAlert): Promise<void> {
        try {
          // Log classification-specific alerts
          const logger = getClassificationLogger();
          const correlationId = `perf_alert_${alert.id}`;
          
          logger.log(
            alert.severity === 'EMERGENCY' ? ClassificationLogLevel.ERROR : 
            alert.severity === 'CRITICAL' ? ClassificationLogLevel.WARN :
            ClassificationLogLevel.INFO,
            ClassificationEventType.PERFORMANCE_METRIC_RECORDED,
            `Performance alert triggered: ${alert.title}`,
            correlationId,
            {
              alertId: alert.id,
              alertTrigger: alert.trigger,
              severity: alert.severity,
              metric: alert.metric,
              currentValue: alert.currentValue,
              threshold: alert.thresholdValue,
              context: alert.context
            }
          );

          // Handle critical alerts that affect classification service
          if (alert.severity === 'CRITICAL' || alert.severity === 'EMERGENCY') {
            if (alert.metric === 'errorRate' && alert.currentValue > 0.5) {
              // High error rate - consider disabling sync processing temporarily
              logger.warn(
                ClassificationEventType.FALLBACK_TRIGGERED,
                'High error rate detected, considering sync processing limits',
                correlationId,
                { errorRate: alert.currentValue, threshold: alert.thresholdValue }
              );
            }
            
            if (alert.metric === 'averageResponseTime' && alert.currentValue > 30000) {
              // Very high response times - force async mode
              logger.warn(
                ClassificationEventType.TIMEOUT_OCCURRED,
                'Extremely high response times detected, forcing async mode',
                correlationId,
                { responseTime: alert.currentValue, threshold: alert.thresholdValue }
              );
            }
          }
        } catch (error) {
          databaseLogger.error('Failed to handle classification performance alert', {
            operation: 'handlePerformanceAlert',
            alertId: alert.id,
            error: {
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined
            }
          });
        }
      },
      async handleResolution(alert: PerformanceAlert): Promise<void> {
        try {
          const logger = getClassificationLogger();
          const correlationId = `perf_alert_resolved_${alert.id}`;
          logger.info(
            ClassificationEventType.PERFORMANCE_METRIC_RECORDED,
            `Performance alert resolved: ${alert.title}`,
            correlationId,
            {
              alertId: alert.id,
              metric: alert.metric,
              resolvedAt: alert.resolvedAt,
              alertResolved: true
            }
          );
        } catch (error) {
          databaseLogger.error('Failed to handle classification performance alert resolution', {
            operation: 'handlePerformanceAlertResolution',
            alertId: alert.id,
            error: {
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined
            }
          });
        }
      }
    };

    // Register the alert handler
    performanceAlerting.addNotificationHandler(classificationAlertHandler);
    
    // Start performance monitoring
    performanceAlerting.startMonitoring();
    
    this.logger.info(
      'Performance alerting initialized for classification service',
      'initializePerformanceAlerting'
    );
  }

  /**
   * Estimate processing difficulty for a prompt
   * @param promptText The prompt text to analyze
   * @returns Difficulty score (0-1)
   */
  private estimateProcessingDifficulty(promptText: string): number {
    let difficulty = 0.3; // Base difficulty

    // Length factor
    if (promptText.length > 500) difficulty += 0.2;
    if (promptText.length > 1000) difficulty += 0.2;

    // Complexity indicators
    const complexKeywords = ['complex', 'detailed', 'comprehensive', 'analyze', 'evaluate', 'compare'];
    const foundKeywords = complexKeywords.filter(keyword => 
      promptText.toLowerCase().includes(keyword)
    ).length;
    difficulty += (foundKeywords / complexKeywords.length) * 0.3;

    // Technical content indicators
    const technicalPatterns = [/\b\w+\.\w+\b/, /\b[A-Z]{2,}\b/, /\d+\.\d+/, /[{}[\]()]/];
    const technicalScore = technicalPatterns.filter(pattern => pattern.test(promptText)).length;
    difficulty += (technicalScore / technicalPatterns.length) * 0.2;

    return Math.max(0, Math.min(1, difficulty));
  }

  /**
   * Get historical performance for similar prompts
   * @param promptText The prompt text to analyze
   * @returns Performance score (0-1) or undefined if no data
   */
  private getHistoricalPerformance(promptText: string): number | undefined {
    // Simple heuristic based on prompt characteristics
    // In a real implementation, this would query historical classification data
    
    const promptLength = promptText.length;
    const categories = this.categoryLoader.getCategories();
    
    // Estimate based on length and available categories
    if (promptLength < 50) {
      return 0.9; // Short prompts usually classify well
    } else if (promptLength < 200) {
      return 0.8; // Medium prompts usually good
    } else if (promptLength < 500) {
      return 0.7; // Longer prompts more variable
    } else {
      return 0.6; // Very long prompts can be challenging
    }
  }
  
  /**
   * Get adaptive timeout based on performance monitoring data
   * Falls back to configured timeout if no data available
   * @returns Timeout in milliseconds
   */
  private getAdaptiveTimeout(): number {
    try {
      // Get adaptive timeout from performance monitor
      const adaptiveTimeout = performanceMonitor.getAdaptiveTimeout('classification');
      
      // Use adaptive timeout if available, otherwise use configured timeout
      if (adaptiveTimeout > 0) {
        this.logger.debug('Using adaptive timeout', 'getAdaptiveTimeout', {
          adaptiveTimeoutMs: adaptiveTimeout,
          configuredTimeoutMs: this.config.syncTimeoutMs,
          source: 'performance_monitor'
        });
        return adaptiveTimeout;
      } else {
        this.logger.debug('Using configured timeout (no adaptive data)', 'getAdaptiveTimeout', {
          configuredTimeoutMs: this.config.syncTimeoutMs,
          source: 'configuration'
        });
        return this.config.syncTimeoutMs;
      }
    } catch (error) {
      // Fallback to configured timeout if performance monitor fails
      this.logger.warn('Failed to get adaptive timeout, using configured', 'getAdaptiveTimeout', {
        error: error instanceof Error ? error.message : String(error),
        fallbackTimeoutMs: this.config.syncTimeoutMs
      });
      return this.config.syncTimeoutMs;
    }
  }

  /**
   * Comprehensive input validation for classification requests
   * @param promptId Prompt ID to validate
   * @param promptText Prompt text to validate
   * @param options Classification options to validate
   * @throws ClassificationValidationError if validation fails
   */
  private validateClassificationInput(
    promptId: string,
    promptText: string,
    options: Partial<ClassificationPromptRequest>
  ): void {
    // Validate promptId
    if (!promptId || typeof promptId !== 'string') {
      throw new ClassificationValidationError('Invalid promptId: must be a non-empty string');
    }

    if (promptId.trim().length === 0) {
      throw new ClassificationValidationError('Invalid promptId: cannot be empty or whitespace only');
    }

    // Validate promptText
    if (!promptText || typeof promptText !== 'string') {
      throw new ClassificationValidationError('Invalid promptText: must be a non-empty string');
    }

    if (promptText.trim().length === 0) {
      throw new ClassificationValidationError('Invalid promptText: cannot be empty or whitespace only');
    }

    // Check prompt length limits
    if (promptText.length > 10000) {
      throw new ClassificationValidationError(`Invalid promptText: exceeds maximum length of 10000 characters (current: ${promptText.length})`);
    }

    if (promptText.length < 3) {
      throw new ClassificationValidationError(`Invalid promptText: must be at least 3 characters long (current: ${promptText.length})`);
    }

    // Validate prompt content for potential injection attempts
    const suspiciousPatterns = [
      /(^|\n)\s*System:/gi,
      /(^|\n)\s*Assistant:/gi,
      /(^|\n)\s*User:/gi,
      /```[\s\S]*```/g, // Code blocks that might be used for injection
      /<script[\s\S]*?<\/script>/gi,
      /javascript:/gi,
      /data:text\/html/gi
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(promptText)) {
        throw new ClassificationValidationError('Invalid promptText: contains potentially malicious content');
      }
    }

    // Validate options if provided
    if (options && typeof options !== 'object') {
      throw new ClassificationValidationError('Invalid options: must be an object');
    }

    // Validate specific option fields if present
    if (options.promptText && options.promptText !== promptText) {
      throw new ClassificationValidationError('Invalid options: promptText in options must match the main promptText parameter');
    }
  }

  /**
   * Validate service health and availability
   * @throws ClassificationServiceUnavailableError if critical services are unavailable
   */
  private validateServiceHealth(): void {
    // Check if service is shutting down
    if (this.isShuttingDown) {
      throw new ClassificationServiceUnavailableError('Classification service is shutting down');
    }

    // Check category loader health
    const categoryDiagnostics = this.categoryLoader.getDiagnostics();
    if (!categoryDiagnostics.health.isHealthy) {
      throw new ClassificationServiceUnavailableError('Category loader is unhealthy');
    }

    // Check if we have categories available
    const categories = this.categoryLoader.getCategories();
    if (categories.length === 0) {
      throw new ClassificationServiceUnavailableError('No categories available for classification');
    }

    // Check Mistral API service health
    const mistralHealth = this.mistralApiService.getHealth();
    if (!mistralHealth.isHealthy) {
      this.logger.warn('Mistral API service is unhealthy', 'validateServiceHealth', {
        health: mistralHealth
      });
      // Don't throw error - allow degraded operation
    }

    // Check async queue overload
    if (this.asyncQueue.length > 100) {
      throw new ClassificationServiceUnavailableError('Service overloaded: async queue too long');
    }

    // Check if we're at maximum concurrent operations
    if (this.currentAsyncOperations >= this.config.maxConcurrentAsync) {
      this.logger.warn('Service at maximum capacity', 'validateServiceHealth', {
        currentOperations: this.currentAsyncOperations,
        maxConcurrent: this.config.maxConcurrentAsync
      });
      // Don't throw error - allow queueing
    }
  }

  /**
   * Enhanced error handling for API-related errors
   * @param error Error from API call
   * @param context Error context
   * @returns Processed error with enhanced information
   */
  private enhanceApiError(error: any, context: string): Error {
    if (!error) {
      return new ClassificationApiError('Unknown API error', undefined, context);
    }

    // Handle rate limiting
    if (error.status === 429 || error.message?.includes('rate limit')) {
      const retryAfter = error.headers?.['retry-after'] ? parseInt(error.headers['retry-after']) * 1000 : undefined;
      return new ClassificationRateLimitError(retryAfter, context);
    }

    // Handle different HTTP status codes
    if (error.status) {
      switch (error.status) {
        case 400:
          return new ClassificationValidationError(`API validation error: ${error.message || 'Bad request'}`, context);
        case 401:
          return new ClassificationApiError('API authentication failed', 401, context);
        case 403:
          return new ClassificationApiError('API access forbidden', 403, context);
        case 404:
          return new ClassificationApiError('API endpoint not found', 404, context);
        case 500:
          return new ClassificationApiError('API server error', 500, context);
        case 502:
          return new ClassificationApiError('API bad gateway', 502, context);
        case 503:
          return new ClassificationServiceUnavailableError('API service unavailable', context);
        default:
          return new ClassificationApiError(`API error: ${error.message || 'Unknown error'}`, error.status, context);
      }
    }

    // Handle network errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.code === 'ETIMEDOUT') {
      return new ClassificationApiError(`Network error: ${error.message}`, undefined, context);
    }

    // Handle timeout errors specifically
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      return new ClassificationTimeoutError(this.config.syncTimeoutMs, context);
    }

    // Default to generic API error
    return new ClassificationApiError(
      error.message || 'Unknown API error',
      error.status,
      context
    );
  }

  /**
   * Enhanced error handling for database-related errors
   * @param error Database error
   * @param context Error context
   * @returns Processed database error
   */
  private enhanceDatabaseError(error: any, context: string): ClassificationDatabaseError {
    if (!error) {
      return new ClassificationDatabaseError('Unknown database error', context);
    }

    // Handle specific database error types
    if (error.message?.includes('connection')) {
      return new ClassificationDatabaseError(`Database connection error: ${error.message}`, context);
    }

    if (error.message?.includes('timeout')) {
      return new ClassificationDatabaseError(`Database timeout: ${error.message}`, context);
    }

    if (error.message?.includes('permission') || error.message?.includes('auth')) {
      return new ClassificationDatabaseError(`Database permission error: ${error.message}`, context);
    }

    if (error.message?.includes('constraint') || error.message?.includes('foreign key')) {
      return new ClassificationDatabaseError(`Database constraint error: ${error.message}`, context);
    }

    // Generic database error
    return new ClassificationDatabaseError(
      error.message || 'Unknown database error',
      context
    );
  }

  /**
   * Graceful degradation strategy for service failures
   * @param error The error that occurred
   * @param context Classification context
   * @returns Fallback result or re-throws if no fallback possible
   */
  private async handleGracefulDegradation(error: Error, context: ClassificationContext): Promise<ClassificationResult | null> {
    this.logger.warn('Attempting graceful degradation', 'handleGracefulDegradation', {
      requestId: context.requestId,
      errorType: error.constructor.name,
      errorMessage: error.message
    });

    // For validation errors, no fallback possible
    if (error instanceof ClassificationValidationError) {
      return null;
    }

    // For API errors, try local fallback classification if possible
    if (error instanceof ClassificationApiError || error instanceof ClassificationRateLimitError) {
      try {
        // Try to classify based on simple keyword matching
        const fallbackCategory = await this.attemptKeywordFallback(context.promptText);
        if (fallbackCategory) {
          const result: ClassificationResult = {
            context: {
              ...context,
              status: ClassificationStatus.COMPLETED,
              categoryId: fallbackCategory.id,
              categoryName: fallbackCategory.name
            },
            success: true,
            categoryId: fallbackCategory.id,
            categoryName: fallbackCategory.name,
            processingTimeMs: this.performanceNow() - context.startTime,
            processingMode: ProcessingMode.SYNC,
            confidence: 0.3 // Low confidence for keyword-based classification
          };

          this.logger.info('Applied keyword-based fallback classification', 'handleGracefulDegradation', {
            requestId: context.requestId,
            categoryId: fallbackCategory.id,
            categoryName: fallbackCategory.name
          });

          return result;
        }
      } catch (fallbackError) {
        this.logger.warn('Keyword fallback also failed', 'handleGracefulDegradation', {
          requestId: context.requestId,
          fallbackError: fallbackError instanceof Error ? fallbackError.message : String(fallbackError)
        });
      }
    }

    // Default fallback: assign default category
    const defaultCategory = this.categoryLoader.getCategoryById(this.config.defaultCategoryId);
    return {
      context: {
        ...context,
        status: ClassificationStatus.COMPLETED,
        categoryId: this.config.defaultCategoryId,
        categoryName: defaultCategory?.name || 'Other'
      },
      success: false,
      categoryId: this.config.defaultCategoryId,
      categoryName: defaultCategory?.name || 'Other',
      processingTimeMs: this.performanceNow() - context.startTime,
      processingMode: ProcessingMode.FAILED,
      error
    };
  }

  /**
   * Simple keyword-based fallback classification
   * @param promptText Text to classify
   * @returns Category if matched, null otherwise
   */
  private async attemptKeywordFallback(promptText: string): Promise<{ id: number; name: string } | null> {
    const text = promptText.toLowerCase();
    const categories = this.categoryLoader.getCategories();

    // Simple keyword mapping for common categories
    const keywordMappings = [
      { keywords: ['math', 'calculation', 'formula', 'equation', 'solve', 'compute'], categoryName: 'Math' },
      { keywords: ['code', 'programming', 'function', 'variable', 'algorithm', 'debug'], categoryName: 'Coding' },
      { keywords: ['write', 'essay', 'story', 'poem', 'letter', 'article'], categoryName: 'Writing' },
      { keywords: ['help', 'how to', 'tutorial', 'guide', 'explain', 'teach'], categoryName: 'General' },
      { keywords: ['translate', 'language', 'français', 'español', 'deutsch'], categoryName: 'Language' }
    ];

    for (const mapping of keywordMappings) {
      const hasKeyword = mapping.keywords.some(keyword => text.includes(keyword));
      if (hasKeyword) {
        const category = categories.find(cat => cat.name.toLowerCase() === mapping.categoryName.toLowerCase());
        if (category) {
          return { id: category.id, name: category.name };
        }
      }
    }

    return null;
  }
} 